import DOMPurify from 'dompurify';

if (!Uint8Array.prototype.slice) {
	Object.defineProperty(Uint8Array.prototype, 'slice', {
		value: function (begin, end) {
			return new Uint8Array(Array.prototype.slice.call(this, begin, end));
		}
	});
}

if (!String.prototype.format) {
	String.prototype.format = function () {
		var args = arguments;
		return this.replace(/{(\d+)}/g, function (match, number) {
			return typeof args[number] != 'undefined'
				? args[number]
				: match
				;
		});
	};
}

declare global {

	interface String {
		replaceAllWithPattern(search: string | RegExp, replacement: string): string;

		sanitizeHtml(): string;

		format(...replacements: string[]): string;

		toTitleCase(): string;
	}

	interface StringConstructor {
		isNullOrEmpty(value: string | undefined | null): boolean;

		isNullOrWhiteSpace(value: string | undefined | null): boolean;

		isString(value: any): value is string;

		empty(): string;

		encode(str: string): Uint8Array;

		parseJwt<T>(token: string): T;
	}
}

var isNullWhitespaceOrEmptyPolyfill = () => {
	String.isNullOrEmpty = function (value: string | undefined | null): boolean {
		return value === undefined || value === null || value.length === 0;
	}

	String.isNullOrWhiteSpace = function (value: string | undefined | null): boolean {
		return value === undefined || value === null || value.length === 0 || /^\s+$/.test(value);
	}

	String.isString = function (value: any): value is string {
		return typeof value === 'string';
	}

	String.empty = function (): string {
		return '';
	}

	String.prototype.replaceAllWithPattern = function (search: string | RegExp, replacement: string): string {
		let target: string = this;
		return target.split(new RegExp(search)).join(replacement)
	}

	String.prototype.sanitizeHtml = function (): string {
		return DOMPurify.sanitize(this, {
			ALLOWED_TAGS: ['img', 'br', 'b', 'i', 'em', 'strong', 'a', 'h1', 'h2', 'h3', 'h4', 'h5', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'div', 'label', 'span', 'u']
		});
	}

	String.prototype.format = function (...replacements: string[]): string {
		var args = replacements;
		return this.replace(/{(\d+)}/g, function (match, number) {
			return typeof args[number] !== undefined
				? args[number]
				: match
				;
		});
	};

	String.prototype.toTitleCase = function (): string {
		return this.replace(/\b(\w+'?\w+)/g, function (match) {
			return match.charAt(0).toUpperCase() + match.substr(1).toLowerCase();
		});
	};
}

var includesPolyfill = () => {
	if (!String.prototype.includes) {
		Object.defineProperty(String.prototype, 'includes', {
			value: function (search, start) {
				if (typeof start !== 'number') {
					start = 0
				}

				if (start + search.length > this.length) {
					return false
				} else {
					return this.indexOf(search, start) !== -1
				}
			}
		})
	}
}

var startsWithPolyfill = () => {
	if (!String.prototype.startsWith) {
		String.prototype.startsWith = function (search, pos) {
			return this.substr(!pos || pos < 0 ? 0 : +pos, search.length) === search;
		};
	}
}

var endsWithPolyfill = () => {
	if (!String.prototype.endsWith) {
		String.prototype.endsWith = function (search, this_len) {
			if (this_len === undefined || this_len > this.length) {
				this_len = this.length;
			}
			return this.substring((this_len ? this_len : 0) - search.length, this_len) === search;
		};
	}
}

var repeatPolyfill = () => {
	if (!String.prototype.repeat) {
		String.prototype.repeat = function (count) {
			'use strict';
			if (this == null)
				throw new TypeError('can\'t convert ' + this + ' to object');

			var str = '' + this;
			// To convert string to integer.
			count = +count;
			// Check NaN
			if (count != count)
				count = 0;

			if (count < 0)
				throw new RangeError('repeat count must be non-negative');

			if (count == Infinity)
				throw new RangeError('repeat count must be less than infinity');

			count = Math.floor(count);
			if (str.length == 0 || count == 0)
				return '';

			// Ensuring count is a 31-bit integer allows us to heavily optimize the
			// main part. But anyway, most current (August 2014) browsers can't handle
			// strings 1 << 28 chars or longer, so:
			if (str.length * count >= 1 << 28)
				throw new RangeError('repeat count must not overflow maximum string size');

			var maxCount = str.length * count;
			count = Math.floor(Math.log(count) / Math.log(2));
			while (count) {
				str += str;
				count--;
			}
			str += str.substring(0, maxCount - str.length);
			return str;
		}
	}
}

var encodePolyfill = () => {
	String.encode = (str: string): Uint8Array => {
		"use strict";
		var Len = str.length, resPos = -1;
		// The Uint8Array's length must be at least 3x the length of the string because an invalid UTF-16
		//  takes up the equivelent space of 3 UTF-8 characters to encode it properly. However, Array's
		//  have an auto expanding length and 1.5x should be just the right balance for most uses.
		var resArr = new Uint8Array(Len * 3);
		for (var point = 0, nextcode = 0, i = 0; i !== Len;) {
			point = str.charCodeAt(i), i += 1;
			if (point >= 0xD800 && point <= 0xDBFF) {
				if (i === Len) {
					resArr[resPos += 1] = 0xef/*0b11101111*/; resArr[resPos += 1] = 0xbf/*0b10111111*/;
					resArr[resPos += 1] = 0xbd/*0b10111101*/; break;
				}
				// https://mathiasbynens.be/notes/javascript-encoding#surrogate-formulae
				nextcode = str.charCodeAt(i);
				if (nextcode >= 0xDC00 && nextcode <= 0xDFFF) {
					point = (point - 0xD800) * 0x400 + nextcode - 0xDC00 + 0x10000;
					i += 1;
					if (point > 0xffff) {
						resArr[resPos += 1] = (0x1e/*0b11110*/ << 3) | (point >>> 18);
						resArr[resPos += 1] = (0x2/*0b10*/ << 6) | ((point >>> 12) & 0x3f/*0b00111111*/);
						resArr[resPos += 1] = (0x2/*0b10*/ << 6) | ((point >>> 6) & 0x3f/*0b00111111*/);
						resArr[resPos += 1] = (0x2/*0b10*/ << 6) | (point & 0x3f/*0b00111111*/);
						continue;
					}
				} else {
					resArr[resPos += 1] = 0xef/*0b11101111*/; resArr[resPos += 1] = 0xbf/*0b10111111*/;
					resArr[resPos += 1] = 0xbd/*0b10111101*/; continue;
				}
			}
			if (point <= 0x007f) {
				resArr[resPos += 1] = (0x0/*0b0*/ << 7) | point;
			} else if (point <= 0x07ff) {
				resArr[resPos += 1] = (0x6/*0b110*/ << 5) | (point >>> 6);
				resArr[resPos += 1] = (0x2/*0b10*/ << 6) | (point & 0x3f/*0b00111111*/);
			} else {
				resArr[resPos += 1] = (0xe/*0b1110*/ << 4) | (point >>> 12);
				resArr[resPos += 1] = (0x2/*0b10*/ << 6) | ((point >>> 6) & 0x3f/*0b00111111*/);
				resArr[resPos += 1] = (0x2/*0b10*/ << 6) | (point & 0x3f/*0b00111111*/);
			}
		}
		return resArr.slice(0, resPos + 1);
	}
}

var parseJwtPolyfill = () => {
	String.parseJwt = function <T>(token: string): T {
		var base64Url = token.split('.')[1];
		var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
		var jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
			return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
		}).join(''));

		return JSON.parse(jsonPayload);
	}
}

var setupStringPolyFills = () => {
	isNullWhitespaceOrEmptyPolyfill();
	includesPolyfill();
	startsWithPolyfill();
	endsWithPolyfill();
	repeatPolyfill();
	encodePolyfill();
	parseJwtPolyfill();
}

export default setupStringPolyFills;